import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Button, TextField, LinearProgress, CircularProgress } from "@material-ui/core";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import "./InvoicePaginationToolbar.css";

@inject("invoiceStore", "profileStore")
@observer
class InvoicePaginationToolbar extends Component {
    state = {
        pageInputValue: "1"
    };

    componentDidMount() {
        this.setState({ pageInputValue: String(this.getCurrentPage()) });
    }

    componentDidUpdate(prevProps) {
        const currentPage = this.getCurrentPage();
        const prevPage = this.props.type === "outgoing" 
            ? prevProps.invoiceStore.outgoingPage 
            : prevProps.invoiceStore.incomingPage;
        if (prevPage !== currentPage) {
            this.setState({ pageInputValue: String(currentPage) });
        }
    }

    getPaginationState = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.outgoingPaginationState 
            : invoiceStore.incomingPaginationState;
    };

    getInvoices = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.outgoingInvoices 
            : invoiceStore.incomingInvoices;
    };

    getShowMore = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.showMoreOutgoing 
            : invoiceStore.showMoreIncoming;
    };

    getCurrentPage = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.outgoingPage || 1
            : invoiceStore.incomingPage || 1;
    };

    getIsLoading = () => {
        const { invoiceStore, type } = this.props;
        return type === "outgoing" 
            ? invoiceStore.outgoingLoading 
            : invoiceStore.incomingLoading;
    };

    loadPage = (page) => {
        const { invoiceStore, profileStore, type } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        
        if (type === "outgoing") {
            invoiceStore.loadByPayeeIdAndFilter(currentProfile?.id, page);
        } else {
            invoiceStore.loadByPayerIdAndFilter(currentProfile?.id, page);
        }
    };

    handlePreviousPage = () => {
        const currentPage = this.getCurrentPage();
        if (currentPage > 1) {
            this.loadPage(currentPage - 1);
        }
    };

    handleNextPage = () => {
        const showMore = this.getShowMore();
        if (showMore) {
            const currentPage = this.getCurrentPage();
            this.loadPage(currentPage + 1);
        }
    };

    handlePageInputChange = (e) => {
        this.setState({ pageInputValue: e.target.value });
    };

    handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            this.handlePageInputBlur();
        }
    };

    handlePageInputBlur = () => {
        const { pageInputValue } = this.state;
        const pageNum = parseInt(pageInputValue, 10);
        
        if (!isNaN(pageNum) && pageNum >= 1) {
            this.loadPage(pageNum);
        } else {
            this.setState({ pageInputValue: String(this.getCurrentPage()) });
        }
    };

    handleLoadAll = () => {
        const { invoiceStore, profileStore, type } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        
        if (type === "outgoing") {
            const profileId = currentProfile?.myAgencies && currentProfile.myAgencies.length
                ? currentProfile.myAgencies[0].agentId
                : currentProfile?.id;
            invoiceStore.loadAllOutgoingInvoices(profileId);
        } else {
            invoiceStore.loadAllIncomingInvoices(currentProfile?.id);
        }
    };

    handleCancelLoadAll = () => {
        const { invoiceStore, type } = this.props;
        if (type === "outgoing") {
            invoiceStore.cancelLoadAllOutgoing();
        } else {
            invoiceStore.cancelLoadAllIncoming();
        }
    };

    handleResetToPaginated = () => {
        const { invoiceStore, profileStore, type } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        
        if (type === "outgoing") {
            invoiceStore.resetOutgoingPage();
            if (currentProfile?.myAgencies && currentProfile.myAgencies.length) {
                invoiceStore.loadByPayeeIdAndFilter(currentProfile.myAgencies[0].agentId, 1);
            } else {
                invoiceStore.loadByPayeeIdAndFilter(currentProfile?.id, 1);
            }
        } else {
            invoiceStore.resetIncomingPage();
            invoiceStore.loadByPayerIdAndFilter(currentProfile?.id, 1);
        }
    };

    isAllLoaded = () => {
        const invoices = this.getInvoices();
        const showMore = this.getShowMore();
        return !showMore && invoices.length > 0;
    };

    render() {
        const { type } = this.props;
        const paginationState = this.getPaginationState();
        const invoices = this.getInvoices();
        const showMore = this.getShowMore();
        const currentPage = this.getCurrentPage();
        const { isLoadingAll, loadAllProgress, totalLoaded } = paginationState;
        const isLoading = this.getIsLoading();
        const { pageInputValue } = this.state;

        const allLoaded = this.isAllLoaded();
        const itemLabel = "invoices";

        const hasPrevious = currentPage > 1;
        const hasNext = showMore;

        if (isLoadingAll) {
            return (
                <div className="invoice-pagination-toolbar">
                    <div className="invoice-pagination-loading-all">
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={true}
                            className="invoice-load-all-btn loading"
                        >
                            <span className="invoice-css-spinner"></span>
                            <span className="invoice-load-btn-text">Loading...</span>
                        </Button>
                        <span className="invoice-loading-progress-text">
                            <strong>{totalLoaded}</strong> {itemLabel} loaded
                        </span>
                        <Button 
                            size="small" 
                            onClick={this.handleCancelLoadAll}
                            className="invoice-cancel-load-btn"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            );
        }

        if (allLoaded) {
            return (
                <div className="invoice-pagination-toolbar invoice-pagination-toolbar-all-loaded">
                    <div className="invoice-all-loaded-info">
                        <CheckCircleIcon className="invoice-all-loaded-icon" />
                        <span>Showing all <strong>{invoices.length}</strong> {itemLabel}</span>
                    </div>
                    <button 
                        className="invoice-reset-view-link"
                        onClick={this.handleResetToPaginated}
                    >
                        Reset to paginated view
                    </button>
                </div>
            );
        }

        if (!showMore && invoices.length === 0) {
            return null;
        }

        const displayTotal = showMore ? `${invoices.length}+` : invoices.length;

        return (
            <div className="invoice-pagination-toolbar">
                <div className="invoice-pagination-info">
                    Showing <strong>{invoices.length}</strong> of <strong>{displayTotal}</strong> {itemLabel}
                </div>

                <div className="invoice-pagination-controls">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handlePreviousPage}
                        disabled={!hasPrevious || isLoading}
                        className="invoice-pagination-nav-btn"
                    >
                        <NavigateBeforeIcon fontSize="small" />
                        Prev
                    </Button>

                    <div className="invoice-page-selector">
                        <span>Page</span>
                        <TextField
                            value={pageInputValue}
                            onChange={this.handlePageInputChange}
                            onBlur={this.handlePageInputBlur}
                            onKeyDown={this.handlePageInputKeyDown}
                            variant="outlined"
                            size="small"
                            className="invoice-page-input"
                            inputProps={{ style: { textAlign: "center", padding: "6px 8px" } }}
                            disabled={isLoading}
                        />
                        <span className="invoice-total-pages">of {showMore ? `${currentPage}+` : currentPage}</span>
                    </div>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={this.handleNextPage}
                        disabled={!hasNext || isLoading}
                        className="invoice-pagination-nav-btn invoice-pagination-nav-btn-primary"
                    >
                        Next
                        <NavigateNextIcon fontSize="small" />
                    </Button>
                </div>

                {(showMore || isLoadingAll) && (
                    <div className="invoice-load-all-section">
                        <div className="invoice-divider" />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleLoadAll}
                            disabled={isLoading || isLoadingAll}
                            className={`invoice-load-all-btn ${(isLoading || isLoadingAll) ? 'loading' : ''}`}
                        >
                            {(isLoading || isLoadingAll) ? (
                                <span className="invoice-css-spinner"></span>
                            ) : (
                                <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                            )}
                            <span className="invoice-load-btn-text">{(isLoading || isLoadingAll) ? 'Loading...' : 'Load All'}</span>
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default InvoicePaginationToolbar;
